import { apiClient } from './apiClient';
import type { Source } from '../types';
import { Logger } from '../utils/logger';
import { SuggestedSourceType, SOURCES_SUGGEST_TIMEOUT_MS } from '../utils/Constants';

/**
 * Source suggérée par l'IA (route /sources/suggest).
 * Le frontend l'affiche dans une modale cochable avant insertion finale via `create()`.
 *
 * `type` est typé sur l'enum {@link SuggestedSourceType} mais on garde `| string`
 * en sécurité au cas où le LLM renverrait une valeur hors enum — le backend
 * normalise déjà vers `WEBSITE` dans ce cas, donc en pratique on ne devrait
 * jamais recevoir de valeur inattendue.
 */
export interface SuggestedSource {
  name: string;
  url: string;
  type: SuggestedSourceType | string;
  description: string;
}

export interface SuggestSourcesResponse {
  sources: SuggestedSource[];
}

export const sourcesService = {
  /**
   * Suggère 5-10 sources de veille à partir du positionnement + watch topics du space.
   * Le backend rejette en 400 si pas de positioning ou aucun watch topic.
   */
  suggest: async (spaceId: string): Promise<SuggestSourcesResponse> => {
    Logger.info('[SourcesService] Suggesting sources', { spaceId });
    const response = await apiClient.post<SuggestSourcesResponse>(
      '/sources/suggest',
      undefined,
      // Le backend (LLM + validation des URLs) peut prendre ~120 s : le défaut
      // axios (30 s) coupait la requête et SetupExpress affichait 0 source.
      { params: { space_id: spaceId }, timeout: SOURCES_SUGGEST_TIMEOUT_MS },
    );
    Logger.info('[SourcesService] Sources suggested', { count: response.data.sources?.length ?? 0 });
    return response.data;
  },

  getAll: async (spaceId: string): Promise<Source[]> => {
    Logger.debug('[SourcesService] Fetching sources', { spaceId });
    const response = await apiClient.get<BackendSource[]>('/sources/', {
      params: { space_id: spaceId }
    });
    const sources = response.data.map(mapBackendSourceToFrontend);
    Logger.info('[SourcesService] Sources fetched', { spaceId, count: sources.length });
    return sources;
  },

  create: async (spaceId: string, source: Partial<Source>) => {
    Logger.info('[SourcesService] Creating source', {
      spaceId,
      type: source.type,
      hasUrl: !!source.url,
      hasKeywords: !!source.keywords?.length,
    });
    const payload = mapFrontendSourceToBackend(source, spaceId);
    const response = await apiClient.post<BackendSource>('/sources/', payload);
    const createdSource = mapBackendSourceToFrontend(response.data);
    Logger.info('[SourcesService] Source created', { sourceId: createdSource.id, spaceId, type: createdSource.type });
    return createdSource;
  },

  update: async (id: string, sourceData: Partial<Source>) => {
    Logger.info('[SourcesService] Updating source', {
      sourceId: id,
      hasTitle: sourceData.title !== undefined,
      hasUrl: sourceData.url !== undefined,
      hasKeywords: sourceData.keywords !== undefined,
      hasEnabled: sourceData.enabled !== undefined,
    });
    const payload = mapFrontendSourceToBackend(sourceData);
    const response = await apiClient.put<BackendSource>(`/sources/${id}`, payload);
    const updatedSource = mapBackendSourceToFrontend(response.data);
    Logger.info('[SourcesService] Source updated', { sourceId: updatedSource.id, type: updatedSource.type });
    return updatedSource;
  },

  /**
   * Delete a source (actually unlinks it from the user's space).
   * The source is only permanently deleted if no other spaces use it.
   */
  delete: async (id: string, spaceId: string) => {
    Logger.warn('[SourcesService] Deleting/unlinking source', { sourceId: id, spaceId });
    const response = await apiClient.delete(`/sources/${id}`, {
      params: { space_id: spaceId }
    });
    Logger.info('[SourcesService] Source deleted/unlinked', { sourceId: id, spaceId });
    return response.data;
  },

  /**
   * Sync a source manually (refresh its content)
   */
  sync: async (id: string): Promise<Source> => {
    Logger.info('[SourcesService] Syncing source', { sourceId: id });
    const response = await apiClient.post<BackendSource>(`/sources/${id}/sync`);
    const syncedSource = mapBackendSourceToFrontend(response.data);
    Logger.info('[SourcesService] Source synced', { sourceId: syncedSource.id, syncStatus: syncedSource.syncStatus ?? null });
    return syncedSource;
  },

  /**
   * Get the current sync status of a source
   */
  getSyncStatus: async (id: string): Promise<{ status: string; error?: string; lastSync?: Date; newItems?: number }> => {
    Logger.debug('[SourcesService] Fetching source sync status', { sourceId: id });
    const response = await apiClient.get<{
      sync_status: string;
      sync_error?: string;
      last_sync?: string;
      new_items_count?: number;
    }>(`/sources/${id}/status`);
    const syncStatus = {
      status: response.data.sync_status,
      error: response.data.sync_error,
      lastSync: response.data.last_sync ? new Date(response.data.last_sync) : undefined,
      newItems: response.data.new_items_count
    };
    Logger.info('[SourcesService] Source sync status fetched', { sourceId: id, status: syncStatus.status, newItems: syncStatus.newItems ?? 0 });
    return syncStatus;
  },

  detectUrl: async (url: string): Promise<{ type: string | null; title: string | null; channel_id: string | null; feed_url: string | null }> => {
    Logger.debug('[SourcesService] Detecting URL type', { url });
    const response = await apiClient.post<{
      type: string | null;
      title: string | null;
      channel_id: string | null;
      feed_url: string | null;
    }>('/sources/detect-url', { url });
    return response.data;
  },
};

// V4-FRONT-ANY-USAGE-BATCH-2 2026-05-28 — typed backend shape returned by FastAPI
// routes (`app/routes/source.py`). Tous les champs sont optionnels parce que
// l'API peut renvoyer une vue partielle (sync status endpoint, detect-url, etc.)
// et les anciens documents ES n'ont pas tous les champs IA récents.
interface BackendSource {
  id: string;
  title?: string;
  name?: string; // legacy alias pour title
  url?: string;
  type?: Source['type'];
  keywords?: string[];
  enabled?: boolean;
  is_active?: boolean; // legacy alias pour enabled
  last_sync?: string;
  last_fetched?: string; // legacy alias pour last_sync
  created_at?: string;
  channel_id?: string;
  raw_content?: string;
  sync_status?: Source['syncStatus'];
  sync_error?: string;
  items_count?: number;
  new_items_last_sync?: number;
  ai_summary?: string;
  relevance_score?: number;
  key_ideas?: string[];
  post_angles?: string[];
  content_type?: Source['contentType'];
}

type BackendSourcePayload = Record<string, unknown>;

function mapBackendSourceToFrontend(data: BackendSource): Source {
  return {
    id: data.id,
    title: data.title || data.name, // Handle both title and name
    url: data.url,
    type: data.type,
    keywords: data.keywords || [],
    enabled: data.enabled ?? data.is_active, // Handle both enabled and is_active
    lastSync: data.last_sync ? new Date(data.last_sync) : (data.last_fetched ? new Date(data.last_fetched) : undefined),
    createdAt: data.created_at ? new Date(data.created_at) : undefined,
    channelId: data.channel_id,
    rawContent: data.raw_content,
    syncStatus: data.sync_status,
    syncError: data.sync_error,
    itemCount: data.items_count ?? undefined,
    newItemsLastSync: data.new_items_last_sync ?? undefined,
    aiSummary: data.ai_summary,
    relevanceScore: data.relevance_score,
    keyIdeas: data.key_ideas,
    postAngles: data.post_angles,
    contentType: data.content_type
  } as Source;
}

function mapFrontendSourceToBackend(source: Partial<Source>, spaceId?: string): BackendSourcePayload {
  const data: BackendSourcePayload = {};
  if (spaceId) data.space_id = spaceId;
  if (source.title !== undefined) data.title = source.title;
  if (source.url !== undefined) data.url = source.url;
  if (source.type !== undefined) data.type = source.type;
  if (source.keywords !== undefined) data.keywords = source.keywords;
  if (source.enabled !== undefined) data.enabled = source.enabled;
  if (source.channelId !== undefined) data.channel_id = source.channelId;
  if (source.rawContent !== undefined) data.raw_content = source.rawContent;

  return data;
}

